import { useEffect, useRef, useState } from "react";

/**
 * ParticleHero
 * ------------
 * Two noise fields drive the base look:
 *
 *   1. COLOR noise (fbm, mid frequency) → decides hue + fine brightness.
 *      Hue is BLUE-DOMINANT: any noise value u in [0, 0.92] maps to
 *      blue/cyan-blue (200..220°). Magenta (300..330°) only appears when
 *      u >= 0.92; fluorescent green (100..140°) only when u >= 0.95.
 *
 *   2. PRESENCE noise (very low frequency, ~4x lower than color noise) →
 *      decides how visible each dot is. Dark blobs form wherever this
 *      field dips.
 *
 * MOUSE HOVER — "Density Bloom"
 * -----------------------------
 * When the cursor is inside the canvas, dots within a soft radius around
 * it "bloom": both their size AND brightness are locally boosted by an
 * amount that falls off smoothly with distance. Each dot has its own
 * per-frame decay buffer (0.9x per frame), so when the cursor moves,
 * points it just passed keep glowing briefly, producing a comet-like
 * smear trail. The hue is never changed — only size + brightness — so
 * the bloom feels like an organic density flare on top of the noise
 * field, not a color shift. When the cursor leaves, all bloom values
 * naturally fade out.
 */
export default function ParticleHero() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [textReady, setTextReady] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setTextReady(true), 120);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    // ---------------------------------------------------------------
    // 2D Simplex noise (Stefan Gustavson – classic implementation)
    // ---------------------------------------------------------------
    const grad3: [number, number][] = [
      [1, 1], [-1, 1], [1, -1], [-1, -1],
      [1, 0], [-1, 0], [0, 1], [0, -1],
    ];
    const F2 = 0.5 * (Math.sqrt(3) - 1);
    const G2 = (3 - Math.sqrt(3)) / 6;

    const buildPerm = (seed: number) => {
      const p = new Uint8Array(256);
      for (let i = 0; i < 256; i++) p[i] = i;
      let s = seed >>> 0;
      for (let i = 255; i > 0; i--) {
        s = (s * 1664525 + 1013904223) >>> 0;
        const j = s % (i + 1);
        const tmp = p[i];
        p[i] = p[j];
        p[j] = tmp;
      }
      const perm = new Uint8Array(512);
      for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
      return perm;
    };

    const permA = buildPerm(1337);
    const permB = buildPerm(4242);

    const simplex2 = (perm: Uint8Array, x: number, y: number) => {
      const sxy = (x + y) * F2;
      const i = Math.floor(x + sxy);
      const j = Math.floor(y + sxy);
      const t = (i + j) * G2;
      const X0 = i - t;
      const Y0 = j - t;
      const x0 = x - X0;
      const y0 = y - Y0;
      const i1 = x0 > y0 ? 1 : 0;
      const j1 = x0 > y0 ? 0 : 1;
      const x1 = x0 - i1 + G2;
      const y1 = y0 - j1 + G2;
      const x2 = x0 - 1 + 2 * G2;
      const y2 = y0 - 1 + 2 * G2;
      const ii = i & 255;
      const jj = j & 255;
      const gi0 = perm[ii + perm[jj]] & 7;
      const gi1 = perm[ii + i1 + perm[jj + j1]] & 7;
      const gi2 = perm[ii + 1 + perm[jj + 1]] & 7;
      let n0 = 0;
      let n1 = 0;
      let n2 = 0;
      let t0 = 0.5 - x0 * x0 - y0 * y0;
      if (t0 >= 0) {
        t0 *= t0;
        n0 = t0 * t0 * (grad3[gi0][0] * x0 + grad3[gi0][1] * y0);
      }
      let t1 = 0.5 - x1 * x1 - y1 * y1;
      if (t1 >= 0) {
        t1 *= t1;
        n1 = t1 * t1 * (grad3[gi1][0] * x1 + grad3[gi1][1] * y1);
      }
      let t2 = 0.5 - x2 * x2 - y2 * y2;
      if (t2 >= 0) {
        t2 *= t2;
        n2 = t2 * t2 * (grad3[gi2][0] * x2 + grad3[gi2][1] * y2);
      }
      return 70 * (n0 + n1 + n2); // ~[-1, 1]
    };

    // Fractal Brownian motion – 2 octaves is enough for our look
    const fbm = (perm: Uint8Array, x: number, y: number) => {
      let v = 0;
      let amp = 1;
      let ampSum = 0;
      let fx = x;
      let fy = y;
      for (let o = 0; o < 2; o++) {
        v += simplex2(perm, fx, fy) * amp;
        ampSum += amp;
        amp *= 0.5;
        fx *= 2;
        fy *= 2;
      }
      return v / ampSum; // still in ~[-1, 1]
    };

    // ---------------------------------------------------------------
    // HSL -> RGB (h in [0,360], s and l in [0,1])
    // ---------------------------------------------------------------
    const hslToRgb = (
      h: number,
      s: number,
      l: number,
      out: [number, number, number],
    ) => {
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const hp = ((h % 360) + 360) % 360 / 60;
      const x = c * (1 - Math.abs((hp % 2) - 1));
      let r = 0;
      let g = 0;
      let b = 0;
      if (hp < 1) { r = c; g = x; }
      else if (hp < 2) { r = x; g = c; }
      else if (hp < 3) { g = c; b = x; }
      else if (hp < 4) { g = x; b = c; }
      else if (hp < 5) { r = x; b = c; }
      else { r = c; b = x; }
      const m = l - c / 2;
      out[0] = (r + m) * 255;
      out[1] = (g + m) * 255;
      out[2] = (b + m) * 255;
    };

    const smoothstep = (edge0: number, edge1: number, v: number) => {
      const t = Math.max(0, Math.min(1, (v - edge0) / (edge1 - edge0)));
      return t * t * (3 - 2 * t);
    };

    // ---------------------------------------------------------------
    // Layout / grid state
    // ---------------------------------------------------------------
    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const SPACING = 10;
    const DOT_RADIUS = 3.1;
    let cols = 0;
    let rows = 0;

    // Per-point bloom buffer (persists across frames, decays each frame)
    let bloom: Float32Array = new Float32Array(0);

    const resize = () => {
      const rect = container.getBoundingClientRect();
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / SPACING) + 1;
      rows = Math.ceil(height / SPACING) + 1;
      bloom = new Float32Array(cols * rows);
    };

    resize();
    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    // ---------------------------------------------------------------
    // Mouse tracking
    // ---------------------------------------------------------------
    // Pointer position in local canvas coordinates. `active = false`
    // means the cursor is currently outside; existing bloom will still
    // decay naturally.
    const mouse = { x: 0, y: 0, active: false };

    const updateMouseFromEvent = (clientX: number, clientY: number) => {
      const rect = container.getBoundingClientRect();
      mouse.x = clientX - rect.left;
      mouse.y = clientY - rect.top;
      mouse.active = true;
    };

    const onMouseMove = (e: MouseEvent) => {
      updateMouseFromEvent(e.clientX, e.clientY);
    };
    const onMouseLeave = () => {
      mouse.active = false;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        updateMouseFromEvent(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const onTouchEnd = () => {
      mouse.active = false;
    };

    container.addEventListener("mousemove", onMouseMove);
    container.addEventListener("mouseleave", onMouseLeave);
    container.addEventListener("touchmove", onTouchMove, { passive: true });
    container.addEventListener("touchend", onTouchEnd);
    container.addEventListener("touchcancel", onTouchEnd);

    // ---------------------------------------------------------------
    // Noise -> hue mapping – BLUE DOMINATED
    // ---------------------------------------------------------------
    const noiseToHue = (u: number, jitter: number) => {
      if (u >= 0.95) {
        const inner = (u - 0.95) / 0.05;
        return 100 + inner * 40 + jitter * 8; // 100..140
      }
      if (u >= 0.92) {
        const inner = (u - 0.92) / 0.03;
        return 300 + inner * 30 + jitter * 8; // 300..330
      }
      const inner = u / 0.92;
      return 200 + inner * 20 + jitter * 6; // 200..220
    };

    const rgb: [number, number, number] = [0, 0, 0];

    // ---------------------------------------------------------------
    // Main loop
    // ---------------------------------------------------------------
    let rafId = 0;
    const startTs = performance.now();

    // Bloom parameters
    const BLOOM_RADIUS = 170;          // px – outer soft radius around cursor
    const BLOOM_DECAY = 0.9;           // per-frame multiplier for existing bloom
    const BLOOM_LIGHT_GAIN = 0.55;     // max additive lightness boost at cursor
    const BLOOM_SIZE_GAIN = 2.6;       // max additive dot-radius (px) at cursor
    const BLOOM_COEFF_GAIN = 0.75;     // extra visibility gain (punch through dark)

    const render = (ts: number) => {
      const time = (ts - startTs) * 0.00018; // slow drift

      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);

      const FREQ_MAIN = 0.055;
      const FREQ_HUE = 0.14;
      const FREQ_LIGHT = 0.09;
      const FREQ_PRESENCE = 0.014;

      const PRESENCE_DARK = 0.34;
      const PRESENCE_BRIGHT = 0.72;
      const PRESENCE_FLOOR = 0.03;

      const SPARK_U = 0.9;

      const bloomR2 = BLOOM_RADIUS * BLOOM_RADIUS;

      for (let j = 0; j < rows; j++) {
        const py = j * SPACING;
        for (let i = 0; i < cols; i++) {
          const px = i * SPACING;
          const idx = j * cols + i;

          const nMain = fbm(
            permA,
            i * FREQ_MAIN + time,
            j * FREQ_MAIN - time * 0.7,
          );
          const nHue = simplex2(
            permB,
            i * FREQ_HUE + 12.5 + time * 0.6,
            j * FREQ_HUE - 8.2 - time * 0.4,
          );
          const nLight = simplex2(
            permA,
            i * FREQ_LIGHT - 30 + time * 0.5,
            j * FREQ_LIGHT + 15 - time * 0.3,
          );

          const nPresence = fbm(
            permB,
            i * FREQ_PRESENCE + 77 - time * 0.25,
            j * FREQ_PRESENCE - 33 + time * 0.15,
          );
          const p = nPresence * 0.5 + 0.5;
          const u = (nMain + 1) * 0.5;

          const hue = noiseToHue(u, nHue);
          const satUnit = (nLight * 0.5 + 0.5);
          const sat = 0.85 + satUnit * 0.15;
          let light = 0.55 + (nHue * 0.5 + 0.5) * 0.10;

          let coeff = PRESENCE_FLOOR
            + (1 - PRESENCE_FLOOR)
              * smoothstep(PRESENCE_DARK, PRESENCE_BRIGHT, p);

          if (u > SPARK_U) {
            const sparkStrength = (u - SPARK_U) / (1 - SPARK_U);
            const sparkCoeff = 0.15 + sparkStrength * 0.55;
            if (sparkCoeff > coeff) coeff = sparkCoeff;
          }

          // ---- Bloom buffer update (per-point smear trail) ----
          // 1) Decay previous bloom value
          let b = bloom[idx] * BLOOM_DECAY;
          // 2) If cursor is on-canvas and close enough, compute a fresh
          //    contribution and take the max — this way freshly-hovered
          //    points get re-lit while just-left points still glow.
          if (mouse.active) {
            const dx = px - mouse.x;
            const dy = py - mouse.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < bloomR2) {
              const d = Math.sqrt(d2);
              const contrib = 1 - smoothstep(0, BLOOM_RADIUS, d);
              if (contrib > b) b = contrib;
            }
          }
          bloom[idx] = b;

          // Apply bloom: bumps visibility coefficient, lightness, and
          // dot size — but NEVER hue.
          if (b > 0) {
            coeff = Math.min(1, coeff + b * BLOOM_COEFF_GAIN);
            light = Math.min(0.95, light + b * BLOOM_LIGHT_GAIN);
          }

          const finalLight = light * coeff;

          hslToRgb(hue, sat, finalLight, rgb);

          const r = rgb[0] | 0;
          const gc = rgb[1] | 0;
          const bc = rgb[2] | 0;

          const radius = DOT_RADIUS + finalLight * 0.7 + b * BLOOM_SIZE_GAIN;

          ctx.fillStyle = `rgb(${r},${gc},${bc})`;
          ctx.beginPath();
          ctx.arc(px, py, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      rafId = window.requestAnimationFrame(render);
    };

    rafId = window.requestAnimationFrame(render);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      container.removeEventListener("mousemove", onMouseMove);
      container.removeEventListener("mouseleave", onMouseLeave);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-screen min-h-[640px] overflow-hidden bg-black"
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full block"
      />

      {/* Centered hero text */}
      <div className="relative z-10 flex h-full w-full items-center justify-center px-6 pointer-events-none">
        <div
          className={`max-w-6xl text-center transition-all duration-700 ease-out ${
            textReady
              ? "opacity-100 blur-0 translate-y-0"
              : "opacity-0 blur-md translate-y-2"
          }`}
          style={{
            willChange: "opacity, filter, transform",
            fontFamily:
              "'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif",
          }}
        >
          <h1 className="text-white/90 font-medium leading-[1.1] tracking-tight text-4xl sm:text-5xl md:text-6xl whitespace-nowrap">
            The next era of intelligence.
            <br />
            We back what&apos;s next.
          </h1>

          {/* CTA button group */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 pointer-events-auto">
            <a
              href="#pitch"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-black transition-all duration-300 hover:bg-white/90 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
            >
              <span>Pitch your idea</span>
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"></i>
            </a>
            <a
              href="#portfolio"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 py-3.5 text-sm font-medium text-white/90 backdrop-blur-sm transition-all duration-300 hover:border-white/50 hover:bg-white/10 whitespace-nowrap cursor-pointer"
            >
              <span>Explore portfolio</span>
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}